import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ChevronRight, ShieldCheck, Minus, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCMS } from '../context/CMSContext';
import api from '../api';
import AddressAutocomplete from '../components/AddressAutocomplete';

// NOTE: The site is gated behind `<RequireCustomer>` — anonymous users cannot
// reach /checkout. So we no longer keep a client-storage "draft" of contact
// details for guest checkouts. Logged-in customers have their profile auto-
// fill the form (see effect below); nothing is persisted client-side.

const EMPTY_FORM = {
  customer_name: '', customer_email: '', customer_phone: '', shipping_address: '', notes: '',
};

const customerToForm = (c) => ({
  customer_name: c.full_name || '',
  customer_email: c.email || '',
  customer_phone: c.phone || '',
  shipping_address: c.address || '',
  notes: '',
});

// Fill blank fields from the customer account record — user-typed values win.
const mergeCustomerIntoForm = (f, customer) => ({
  customer_name:     f.customer_name     || customer.full_name || '',
  customer_email:    f.customer_email    || customer.email     || '',
  customer_phone:    f.customer_phone    || customer.phone     || '',
  shipping_address:  f.shipping_address  || customer.address   || '',
  notes:             f.notes,
});

const cartToPayload = (form, items) => ({
  ...form,
  items: items.map((i) => ({
    product_id: i.id, name: i.name, price: i.price, qty: i.qty, image: i.image || '',
  })),
});

const placeOrder = async (form, items) => {
  const { data } = await api.post('/orders', cartToPayload(form, items));
  return data;
};

const CheckoutPage = () => {
  const { items, subtotal, bundleDiscount, total, clear, updateQty, removeItem } = useCart();
  const { customer } = useAuth();
  const { t } = useCMS();
  const navigate = useNavigate();

  const [form, setForm] = useState(() => (customer ? customerToForm(customer) : EMPTY_FORM));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!customer) return;
    setForm((f) => mergeCustomerIntoForm(f, customer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, mergeCustomerIntoForm, setForm]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error('Your cart is empty'); return; }
    setSubmitting(true);
    try {
      const order = await placeOrder(form, items);
      clear();
      navigate(`/pay/${order.reference}`);
    } catch (err) {
      // 409 from the duplicate-order guard — surface the existing reference
      // and offer to take the customer there so they don't double-submit.
      if (err.response?.status === 409) {
        const detail = err.response.data?.detail;
        const existingRef = typeof detail === 'object' ? detail.reference : null;
        if (existingRef) {
          toast.error(`You already have a pending order ${existingRef}. Pay that one first.`, { duration: 7000 });
          clear();
          navigate(`/pay/${existingRef}`);
          return;
        }
      }
      toast.error(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not place order'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="text-2xl font-bold text-[#0a0a0a]">Your cart is empty</h1>
        <p className="mt-2 text-slate-500">Add a product before checking out.</p>
        <Link to="/" className="inline-block mt-6 px-5 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg" data-testid="checkout-empty-shop-btn">
          Continue shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-5xl mx-auto px-6 py-12">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/" className="hover:text-red-700">Home</Link>
        <ChevronRight size={13} />
        <span>Checkout</span>
      </div>
      <h1 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-[#0a0a0a]">Checkout</h1>
      <p className="text-slate-500 mt-2">Confirm your details — you'll pay by PayID on the next step.</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="checkout-form">
          <h2 className="text-lg font-semibold text-[#0a0a0a]">Delivery details</h2>
          <Field label="Full name" value={form.customer_name} onChange={update('customer_name')} required testid="checkout-name" autoComplete="name" name="name" />
          <Field label="Email" type="email" value={form.customer_email} onChange={update('customer_email')} required testid="checkout-email" autoComplete="email" name="email" />
          <Field label="Phone" type="tel" value={form.customer_phone} onChange={update('customer_phone')} required testid="checkout-phone" autoComplete="tel" name="tel" />
          <label className="block">
            <span className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500">Shipping address<span className="text-red-500">*</span></span>
            <div className="mt-1.5">
              <AddressAutocomplete
                value={form.shipping_address}
                onChange={(v) => setForm((f) => ({ ...f, shipping_address: v }))}
                required
                variant="light"
                testid="checkout-address"
              />
            </div>
          </label>
          <Field label="Order notes (optional)" value={form.notes} onChange={update('notes')} testid="checkout-notes" multiline autoComplete="off" name="notes" />

          <button
            type="submit"
            disabled={submitting}
            data-testid="checkout-submit-btn"
            className="w-full mt-2 py-3.5 bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Placing order…</> : 'Continue to PayID payment'}
          </button>
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck size={13} /> Your details are stored securely. No card data is collected.</p>
        </form>

        {/* Summary */}
        <aside className="lg:col-span-2">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="text-sm font-semibold tracking-[0.12em] uppercase text-slate-500">Order summary</h2>
            <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3 items-start" data-testid={`checkout-line-${i.id}`}>
                  <div className="w-14 h-14 rounded-lg bg-slate-50 flex-shrink-0 overflow-hidden">
                    {i.image && <img src={i.image} alt={i.name} className="w-full h-full object-contain p-1" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0a0a0a] line-clamp-2">{i.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">A${i.price.toFixed(2)} each</p>
                    <div className="mt-2 inline-flex items-center bg-white border border-slate-200 rounded-full">
                      <button
                        type="button"
                        onClick={() => updateQty(i.id, i.qty - 1)}
                        className="p-1.5 text-slate-600 hover:text-red-600"
                        aria-label="Decrease quantity"
                        data-testid={`checkout-dec-${i.id}`}
                      ><Minus size={12} /></button>
                      <span className="px-2 text-sm font-bold text-[#0a0a0a] min-w-[28px] text-center" data-testid={`checkout-qty-${i.id}`}>{i.qty}</span>
                      <button
                        type="button"
                        onClick={() => updateQty(i.id, i.qty + 1)}
                        className="p-1.5 text-slate-600 hover:text-red-600"
                        aria-label="Increase quantity"
                        data-testid={`checkout-inc-${i.id}`}
                      ><Plus size={12} /></button>
                      <button
                        type="button"
                        onClick={() => removeItem(i.id)}
                        className="ml-1 p-1.5 text-slate-400 hover:text-red-600 border-l border-slate-200"
                        aria-label="Remove from cart"
                        data-testid={`checkout-remove-${i.id}`}
                      ><X size={12} /></button>
                    </div>
                  </div>
                  <p className="text-sm font-bold text-[#0a0a0a] whitespace-nowrap">A${(i.price * i.qty).toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-slate-100 space-y-2 text-sm">
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
              {bundleDiscount > 0 && <Row label="Bundle discount (15%)" value={`− $${bundleDiscount.toFixed(2)}`} accent />}
              <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-100">
                <span className="text-[#0a0a0a]">Total</span>
                <span className="text-[#0a0a0a]" data-testid="checkout-total">${total.toFixed(2)} AUD</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

const Field = ({ label, value, onChange, required, type = 'text', multiline, testid, autoComplete, name }) => (
  <label className="block">
    <span className="text-xs font-semibold tracking-[0.08em] uppercase text-slate-500">{label}{required && <span className="text-red-500">*</span>}</span>
    {multiline ? (
      <textarea
        value={value} onChange={onChange} required={required} rows={3}
        data-testid={testid}
        autoComplete={autoComplete}
        name={name}
        className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/25 focus:border-red-400 resize-none"
      />
    ) : (
      <input
        type={type} value={value} onChange={onChange} required={required}
        data-testid={testid}
        autoComplete={autoComplete}
        name={name}
        className="mt-1.5 w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/25 focus:border-red-400"
      />
    )}
  </label>
);

const Row = ({ label, value, accent }) => (
  <div className={`flex justify-between ${accent ? 'text-red-700' : 'text-slate-600'}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

export default CheckoutPage;
